// src/components/devices/DeviceManager.js
import React, { useState, useContext, useEffect } from 'react';
import {
  FaPlus,
  FaMapMarkerAlt,
  FaTags,
  FaMicrochip,
  FaSyncAlt,
  FaList,
  FaSearch,
  FaBuilding  // Added for floor icon
} from 'react-icons/fa';
import { AppContext } from '../../context/AppContext';
import { 
  Button, 
  PageHeader, 
  DeviceGrid 
} from '../../design-system/components';

import DeviceAdder from './DeviceAdder';
import BulkDeviceManager from './BulkDeviceManager';
import DeviceCardComponent from './DeviceCard';

const DeviceManager = () => {
  const { devices, refreshDevices } = useContext(AppContext);
  const [isAddingDevice, setIsAddingDevice] = useState(false);
  const [devicesByType, setDevicesByType] = useState({});
  const [devicesByLocation, setDevicesByLocation] = useState({});
  const [devicesByFloor, setDevicesByFloor] = useState({});  // Added floor grouping
  const [viewMode, setViewMode] = useState('type'); // 'type', 'location', 'floor', or 'bulk'
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Group devices by type, location, and floor
  useEffect(() => {
    if (!devices || devices.length === 0) {
      setDevicesByType({});
      setDevicesByLocation({});
      setDevicesByFloor({});  // Added
      setLoading(false);
      return;
    }

    // Filter by search query
    const query = searchQuery.toLowerCase().trim();
    const filtered = query
      ? devices.filter(device => {
          const name = (device.name || device.id || '').toLowerCase();
          const location = (device.location || '').toLowerCase();
          const floor = (device.floor || '').toLowerCase();
          return name.includes(query) || location.includes(query) || floor.includes(query);
        })
      : devices;

    // Group by type
    const byType = {};
    filtered.forEach(device => {
      const type = device.type || 'other';
      if (!byType[type]) {
        byType[type] = [];
      }
      byType[type].push(device);
    });
    
    // Group by location
    const byLocation = {};
    filtered.forEach(device => {
      const location = device.location || 'unknown';
      if (!byLocation[location]) {
        byLocation[location] = [];
      }
      byLocation[location].push(device);
    });
    
    // Group by floor - Added this section
    const byFloor = {};
    filtered.forEach(device => {
      const floor = device.floor || 'unassigned';
      if (!byFloor[floor]) {
        byFloor[floor] = [];
      }
      byFloor[floor].push(device);
    });
    
    setDevicesByType(byType);
    setDevicesByLocation(byLocation);
    setDevicesByFloor(byFloor);  // Added
    setLoading(false);
  }, [devices, searchQuery]);
  
  // Open device adder
  const handleAddDevice = () => {
    setIsAddingDevice(true);
  };
  
  // Close device adder
  const handleCloseDeviceAdder = () => {
    setIsAddingDevice(false);
  };
  
  // Handle successful device addition
  const handleDeviceAdded = async () => {
    try {
      await refreshDevices();
    } catch (error) {
      console.error('Error refreshing devices:', error);
    }
  };
  
  // Toggle view mode - Updated to include floor
  const toggleViewMode = () => {
    setViewMode(prev => {
      if (prev === 'type') return 'location';
      if (prev === 'location') return 'floor';
      if (prev === 'floor') return 'bulk';
      return 'type';
    });
  };
  
  // Handle refresh button click
  const handleRefresh = async () => {
    try {
      setLoading(true);
      await refreshDevices();
    } catch (error) {
      console.error('Error refreshing devices:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Render devices grouped by type
  const renderDevicesByType = () => {
    if (Object.keys(devicesByType).length === 0) {
      return (
        <div className="bg-white rounded-2xl p-12 text-center shadow-sm">
          <div className="text-gray-400 mb-4">
            <FaMicrochip className="mx-auto text-4xl" />
          </div>
          <p className="text-gray-600 text-lg mb-6">No devices found.</p>
          <Button 
            variant="primary"
            onClick={handleAddDevice}
            className="flex items-center gap-2 mx-auto"
          >
            <FaPlus /> Add Your First Device
          </Button>
        </div>
      );
    }
    
    return (
      <div className="space-y-8">
        {Object.entries(devicesByType).map(([type, deviceList]) => (
          <div key={type} className="bg-white rounded-2xl shadow-sm overflow-hidden">
            <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-900">{formatDeviceType(type)}</h2>
              <span className="bg-gray-200 px-3 py-1 rounded-full text-sm font-medium text-gray-600">
                {deviceList.length} {deviceList.length === 1 ? 'device' : 'devices'}
              </span>
            </div>
            <div className="p-6">
              <DeviceGrid>
                {deviceList.map(device => (
                  <DeviceCardComponent key={device.id} deviceId={device.id} device={device} />
                ))}
              </DeviceGrid>
            </div>
          </div>
        ))}
      </div>
    );
  };
  
  // Render devices grouped by location
  const renderDevicesByLocation = () => {
    if (Object.keys(devicesByLocation).length === 0) {
      return (
        <div className="bg-white rounded-2xl p-12 text-center shadow-sm">
          <div className="text-gray-400 mb-4">
            <FaMapMarkerAlt className="mx-auto text-4xl" />
          </div>
          <p className="text-gray-600 text-lg mb-6">No devices found.</p>
          <Button 
            variant="primary"
            onClick={handleAddDevice}
            className="flex items-center gap-2 mx-auto"
          >
            <FaPlus /> Add Your First Device
          </Button>
        </div>
      );
    }
    
    return (
      <div className="space-y-8">
        {Object.entries(devicesByLocation).map(([location, deviceList]) => (
          <div key={location} className="bg-white rounded-2xl shadow-sm overflow-hidden">
            <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-900">{formatLocation(location)}</h2>
              <span className="bg-gray-200 px-3 py-1 rounded-full text-sm font-medium text-gray-600">
                {deviceList.length} {deviceList.length === 1 ? 'device' : 'devices'}
              </span>
            </div>
            <div className="p-6">
              <DeviceGrid>
                {deviceList.map(device => (
                  <DeviceCardComponent key={device.id} deviceId={device.id} device={device} />
                ))}
              </DeviceGrid>
            </div>
          </div>
        ))}
      </div>
    );
  };
  
  // Render devices grouped by floor - Added this function
  const renderDevicesByFloor = () => {
    if (Object.keys(devicesByFloor).length === 0) {
      return (
        <div className="bg-white rounded-2xl p-12 text-center shadow-sm">
          <div className="text-gray-400 mb-4">
            <FaBuilding className="mx-auto text-4xl" />
          </div>
          <p className="text-gray-600 text-lg mb-6">No devices found.</p>
          <Button 
            variant="primary"
            onClick={handleAddDevice}
            className="flex items-center gap-2 mx-auto"
          >
            <FaPlus /> Add Your First Device
          </Button>
        </div>
      );
    }
    
    return (
      <div className="space-y-8">
        {Object.entries(devicesByFloor).map(([floor, deviceList]) => (
          <div key={floor} className="bg-white rounded-2xl shadow-sm overflow-hidden">
            <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-900">{formatFloor(floor)}</h2>
              <span className="bg-gray-200 px-3 py-1 rounded-full text-sm font-medium text-gray-600">
                {deviceList.length} {deviceList.length === 1 ? 'device' : 'devices'}
              </span>
            </div>
            <div className="p-6">
              <DeviceGrid>
                {deviceList.map(device => (
                  <DeviceCardComponent key={device.id} deviceId={device.id} device={device} />
                ))}
              </DeviceGrid>
            </div>
          </div>
        ))}
      </div>
    );
  };
  
  // Format device type for display
  const formatDeviceType = (type) => {
    if (!type) return 'Other';
    const formatted = type.charAt(0).toUpperCase() + type.slice(1);
    return formatted.replace(/([A-Z])/g, ' $1').trim(); // Add space before capital letters
  };
  
  // Format location for display
  const formatLocation = (location) => {
    if (!location || location === 'unknown') return 'Unknown Location';
    return location.charAt(0).toUpperCase() + location.slice(1);
  };
  
  // Format floor for display - Added this function
  const formatFloor = (floor) => {
    if (!floor || floor === 'unassigned') return 'Unassigned Floor';
    if (floor.toLowerCase().includes('floor')) return floor;
    return `Floor ${floor}`;
  };
  
  // If loading devices
  if (loading) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="bg-white rounded-2xl p-12 text-center shadow-sm">
          <div className="animate-spin mx-auto mb-4 h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
          <p className="text-gray-600">Loading devices...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="max-w-7xl mx-auto p-6 bg-gray-50 min-h-screen">
      <PageHeader
        title="My Devices"
        actions={[
          <Button 
            key="view-toggle"
            variant="secondary"
            onClick={toggleViewMode}
            className="flex items-center gap-2"
          >
            {viewMode === 'type' ? <FaMapMarkerAlt /> : 
             viewMode === 'location' ? <FaBuilding /> : 
             viewMode === 'floor' ? <FaList /> : <FaTags />}
            {viewMode === 'type' ? 'By Location' : 
             viewMode === 'location' ? 'By Floor' : 
             viewMode === 'floor' ? 'Bulk Manage' : 'By Type'}
          </Button>,
          <Button 
            key="refresh"
            variant="secondary"
            onClick={handleRefresh}
            disabled={loading}
            className="flex items-center gap-2"
          >
            <FaSyncAlt /> Refresh
          </Button>,
          <Button 
            key="add-device"
            variant="primary"
            onClick={handleAddDevice}
            className="flex items-center gap-2"
          >
            <FaPlus /> Add Device
          </Button>
        ]}
      />
      
      <div className="relative mb-6">
        <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder="Search devices by name, location, or floor..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-11 pr-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-sm"
        />
      </div>

      <div className="space-y-8">
        {viewMode === 'type' && renderDevicesByType()}
        {viewMode === 'location' && renderDevicesByLocation()}
        {viewMode === 'floor' && renderDevicesByFloor()}
        {viewMode === 'bulk' && <BulkDeviceManager />}
      </div>
      
      {isAddingDevice && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <DeviceAdder 
              onClose={handleCloseDeviceAdder}
              onDeviceAdded={handleDeviceAdded}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default DeviceManager;